<template>
  <settings-group-item :label="label" :description="description">
    <div class="controls d-flex align-center my-2">
      <v-switch
        v-model="model"
        class="mr-2 mt-0"
        hide-details
        :disabled="disabled"
      />
      <review-member-management
        v-if="enabled"
        :open.sync="openPopup"
        :review-members="assignedReviewMembers"
        :disabled="disabled"
      >
        <template #popup>
          <assign-review-members-popup
            :title="popupTitle"
            :review-members="reviewMembers"
            :assigned-review-members="assignedReviewMembers"
            @assign="onAssign"
            @unassign="onUnassign"
            @close="openPopup = false"
          />
        </template>
      </review-member-management>
    </div>
    <div
      v-if="enabled && empty && !disableEmptyMessage"
      class="caption grey--text"
    >
      <v-icon class="material-icons-outlined">info</v-icon>
      If no reviewers are assigned this setting will have no effect.
    </div>
  </settings-group-item>
</template>

<script>
import SettingsGroupItem from './SettingsGroupItem';
import ReviewMemberManagement from './ReviewMemberManagement';
import AssignReviewMembersPopup from './AssignReviewMembersPopup';
import { Review } from '@/models';
import { ReviewMemberPermission } from '@/enums';

export default {
  name: 'ReviewMemberAssignmentSetting',
  components: {
    SettingsGroupItem,
    ReviewMemberManagement,
    AssignReviewMembersPopup,
  },
  props: {
    review: {
      type: Review,
      required: true,
    },
    permission: {
      type: String,
      required: true,
      validator: (field) =>
        Object.values(ReviewMemberPermission).includes(field),
    },
    label: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      default: null,
    },
    popupTitle: {
      type: String,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['assign-permission', 'unassign-permission', 'unassign-all'],
  data: () => ({
    enabledByPermission: {},
    openPopupByPermission: {},
    disableEmptyMessageByPermission: {},
    openPopupDelayTimeout: null,
  }),
  computed: {
    model: {
      get: function () {
        this.setupModelForPermission();
        return this.enabled;
      },
      set: function (value) {
        this.$set(this.enabledByPermission, this.permission, value);

        // Prevent the empty message from showing initially.
        this.$set(
          this.disableEmptyMessageByPermission,
          this.permission,
          value
        );

        // Automatically open popup on enable.
        if (value) {
          this.openPopupWithDelay();
          // By default assign the lead reviewer.
          this.onAssign(this.review.leadReviewer);
        }

        if (!value && !this.empty) {
          this.$emit('unassign-all', this.permission);
        }
      },
    },
    openPopup: {
      get: function () {
        return this.openPopupByPermission[this.permission];
      },
      set: function (value) {
        this.$set(this.openPopupByPermission, this.permission, value);
        this.$set(
          this.disableEmptyMessageByPermission,
          this.permission,
          value
        );
      },
    },
    empty() {
      return this.assignedReviewMembers.length === 0;
    },
    reviewMembers() {
      return this.review.reviewMembers;
    },
    assignedReviewMembers() {
      return this.reviewMembers.filter((member) =>
        member.memberPermissions.includes(this.permission)
      );
    },
    enabled() {
      return this.enabledByPermission[this.permission];
    },
    disableEmptyMessage() {
      return this.disableEmptyMessageByPermission[this.permission];
    },
  },
  destroyed() {
    clearTimeout(this.openPopupDelayTimeout);
  },
  methods: {
    setupModelForPermission() {
      const hasModel = this.permission in this.enabledByPermission;
      if (!hasModel) {
        this.$set(
          this.enabledByPermission,
          this.permission,
          !this.empty
        );
      }
    },
    openPopupWithDelay() {
      // Use delay to avoid weird animation artifact.
      clearTimeout(this.openPopupDelayTimeout);
      this.openPopupDelayTimeout = setTimeout(() => {
        this.openPopup = true;
      }, 100);
    },
    onAssign(reviewMember) {
      this.$emit('assign-permission', {
        reviewMember,
        permission: this.permission,
      });
    },
    onUnassign(reviewMember) {
      this.$emit('unassign-permission', {
        reviewMember,
        permission: this.permission,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.controls {
  height: 48px;
}
</style>
